/**
 * Open state of the New session dialog, the remembered choices, and the session we are
 * waiting to appear after a start. The `session.new` command (key n, palette) opens it
 * from anywhere: a page that mounts <NewSessionHost /> shows it; otherwise we go to Sessions.
 */
import type { Id, SessionStartMode } from '@orgus/protocol';
import { create } from 'zustand';

export interface NewSessionPrefill {
  projectId?: Id;
  machineId?: Id;
  checkoutId?: Id;
}

export interface Remembered {
  projectId?: Id;
  machineId?: Id;
  mode?: SessionStartMode;
  permissionMode?: string;
  /** The permission choice per run mode. */
  permissionModes?: Partial<Record<SessionStartMode, string>>;
  model?: string;
  /** The agent last started (claude-code, codex, or an ACP kind such as gemini-cli). */
  agentKind?: string;
  /** Where it ran last: an existing checkout or a new worktree. */
  where?: 'checkout' | 'worktree';
}

export interface PendingStart {
  machineId: Id;
  projectId: Id | null;
  /** When the agent returned the id. */
  sessionId?: Id;
  checkoutId?: Id;
  since: number;
}

interface NewSessionStore {
  open: boolean;
  prefill: NewSessionPrefill;
  /** Mounted hosts; 0 means no page can show the dialog right now. */
  hosts: number;
  pending: PendingStart | null;
}

export const useNewSession = create<NewSessionStore>()(() => ({ open: false, prefill: {}, hosts: 0, pending: null }));

const KEY = 'fd.newSession';

export function readRemembered(): Remembered {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Remembered) : {};
  } catch {
    return {};
  }
}

export function remember(r: Remembered): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(r));
  } catch {
    // private mode: choices last for this tab only
  }
}

/** Open the dialog. On a page without a host, go to Sessions, which has one. */
export function openNewSession(prefill: NewSessionPrefill = {}): void {
  useNewSession.setState({ open: true, prefill });
  if (useNewSession.getState().hosts === 0 && typeof window !== 'undefined') {
    // React Router follows popstate, so this navigates without importing the router here.
    window.history.pushState(null, '', '/sessions');
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
}

export function closeNewSession(): void {
  useNewSession.setState({ open: false });
}

export function setPending(p: PendingStart | null): void {
  useNewSession.setState({ pending: p });
}

export function registerHost(): () => void {
  useNewSession.setState((s) => ({ hosts: s.hosts + 1 }));
  return () => useNewSession.setState((s) => ({ hosts: Math.max(0, s.hosts - 1) }));
}
